/**
 * Analytics Hooks
 * React Query hooks for Analytics and AI Management data fetching
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { analyticsApi, aiManagementApi } from '../api/analyticsApi';
import type {
  AnalyticsSummary,
  SalesRepPerformance,
  FunnelStage,
  TrendDataPoint,
  AIInsight,
  TasksData,
  SalesRepPerformanceData,
  CoachingRecommendation,
  LeadAssignmentSuggestion,
  PredictionsData,
} from '../api/analyticsApi';

// ==================== ANALYTICS HOOKS ====================

/**
 * Fetch analytics summary metrics
 */
export const useAnalyticsSummary = () => {
  return useQuery<AnalyticsSummary>({
    queryKey: ['analytics', 'summary'],
    queryFn: analyticsApi.getSummary,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Fetch sales ranking data
 */
export const useSalesRanking = () => {
  return useQuery<SalesRepPerformance[]>({
    queryKey: ['analytics', 'sales-ranking'],
    queryFn: analyticsApi.getSalesRanking,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Fetch conversion funnel data
 */
export const useConversionFunnel = () => {
  return useQuery<FunnelStage[]>({
    queryKey: ['analytics', 'funnel'],
    queryFn: analyticsApi.getFunnel,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Fetch conversion trends (12 months)
 */
export const useConversionTrends = () => {
  return useQuery<TrendDataPoint[]>({
    queryKey: ['analytics', 'trends'],
    queryFn: analyticsApi.getTrends,
    staleTime: 10 * 60 * 1000, // 10 minutes (historical data)
  });
};

/**
 * Fetch AI insights
 */
export const useAIInsights = () => {
  return useQuery<AIInsight[]>({
    queryKey: ['analytics', 'insights'],
    queryFn: analyticsApi.getInsights,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Fetch task summary
 */
export const useTasksSummary = () => {
  return useQuery<TasksData>({
    queryKey: ['analytics', 'tasks'],
    queryFn: analyticsApi.getTasks,
    staleTime: 2 * 60 * 1000, // 2 minutes (tasks change frequently)
  });
};

// ==================== AI MANAGEMENT HOOKS ====================

/**
 * Fetch performance table data
 */
export const usePerformanceTable = () => {
  return useQuery<SalesRepPerformanceData[]>({
    queryKey: ['ai-management', 'performance-table'],
    queryFn: aiManagementApi.getPerformanceTable,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Fetch coaching recommendations
 */
export const useCoachingRecommendations = () => {
  return useQuery<CoachingRecommendation[]>({
    queryKey: ['ai-management', 'coaching-recommendations'],
    queryFn: aiManagementApi.getCoachingRecommendations,
    staleTime: 10 * 60 * 1000,
  });
};

/**
 * Fetch lead assignment suggestions
 */
export const useLeadAssignments = () => {
  return useQuery<LeadAssignmentSuggestion[]>({
    queryKey: ['ai-management', 'lead-assignments'],
    queryFn: aiManagementApi.getLeadAssignments,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Assign lead mutation
 */
export const useAssignLead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: aiManagementApi.assignLead,
    onSuccess: () => {
      // Invalidate related queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['ai-management', 'lead-assignments'] });
      queryClient.invalidateQueries({ queryKey: ['ai-management', 'performance-table'] });
      queryClient.invalidateQueries({ queryKey: ['analytics', 'summary'] });
      queryClient.invalidateQueries({ queryKey: ['leads'] });
    },
  });
};

/**
 * Fetch performance predictions
 */
export const usePerformancePredictions = () => {
  return useQuery<PredictionsData>({
    queryKey: ['ai-management', 'predictions'],
    queryFn: aiManagementApi.getPredictions,
    staleTime: 10 * 60 * 1000,
  });
};

// ==================== COMBINED HOOKS ====================

/**
 * Fetch all analytics data at once (for Analytics Tab)
 */
export const useAllAnalytics = () => {
  const summary = useAnalyticsSummary();
  const salesRanking = useSalesRanking();
  const funnel = useConversionFunnel();
  const trends = useConversionTrends();
  const insights = useAIInsights();
  const tasks = useTasksSummary();

  return {
    summary,
    salesRanking,
    funnel,
    trends,
    insights,
    tasks,
    isLoading:
      summary.isLoading ||
      salesRanking.isLoading ||
      funnel.isLoading ||
      trends.isLoading ||
      insights.isLoading ||
      tasks.isLoading,
    isError:
      summary.isError ||
      salesRanking.isError ||
      funnel.isError ||
      trends.isError ||
      insights.isError ||
      tasks.isError,
  };
};

/**
 * Fetch all AI management data at once (for AI Management Tab)
 */
export const useAllAIManagement = () => {
  const performanceTable = usePerformanceTable();
  const coachingRecommendations = useCoachingRecommendations();
  const leadAssignments = useLeadAssignments();
  const predictions = usePerformancePredictions();

  return {
    performanceTable,
    coachingRecommendations,
    leadAssignments,
    predictions,
    isLoading:
      performanceTable.isLoading ||
      coachingRecommendations.isLoading ||
      leadAssignments.isLoading ||
      predictions.isLoading,
    isError:
      performanceTable.isError ||
      coachingRecommendations.isError ||
      leadAssignments.isError ||
      predictions.isError,
  };
};
